import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { FilterValue } from './FilterBar';

export interface SavedFilter {
  id: string;
  name: string;
  description?: string;
  filters: FilterValue[] | Record<string, any>;
  visibility?: 'private' | 'public';
  isDefault?: boolean;
  createdBy?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface SavedFiltersListProps {
  savedFilters: SavedFilter[];
  activeFilterId?: string;
  currentUserId?: string;
  onSelect: (filter: SavedFilter) => void;
  onDelete?: (filterId: string) => void;
  onSetDefault?: (filterId: string) => void;
  onUnsetDefault?: (filterId: string) => void;
  onEdit?: (filter: SavedFilter) => void;
  size?: 'sm' | 'md';
  showActions?: boolean;
  groupByVisibility?: boolean;
  className?: string;
}

const SEARCH_THRESHOLD = 8;

/** Padlock glyph — marks private (owner-only) filters. */
const LockGlyph = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

/** Globe glyph — marks filters shared with the whole team. */
const GlobeGlyph = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

export function SavedFiltersList({
  savedFilters,
  activeFilterId,
  currentUserId,
  onSelect,
  onDelete,
  onSetDefault,
  onUnsetDefault,
  onEdit,
  size = 'md',
  showActions = true,
  groupByVisibility = false,
  className,
}: SavedFiltersListProps) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const sizeClasses = {
    sm: 'text-xs py-1.5 px-2',
    md: 'text-sm py-2.5 px-3',
  };

  if (savedFilters.length === 0) {
    return (
      <div className={clsx('text-sm text-gray-500 py-4 text-center dark:text-gray-400', className)}>
        No saved filters
      </div>
    );
  }

  const showSearch = savedFilters.length > SEARCH_THRESHOLD;
  const filteredFilters = (searchQuery
    ? savedFilters.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : savedFilters
  ).slice().sort((a, b) => a.name.localeCompare(b.name));

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const isOwner = (filter: SavedFilter) =>
    !currentUserId || !filter.createdBy || filter.createdBy === currentUserId;

  const getFilterCount = (filter: SavedFilter) => {
    if (Array.isArray(filter.filters)) {
      return filter.filters.length;
    }
    return Object.values(filter.filters).filter(
      (v) => v !== undefined && v !== null && (Array.isArray(v) ? v.length > 0 : true)
    ).length;
  };

  const renderFilter = (filter: SavedFilter, showVisibilityIcon = true) => {
    const owned = isOwner(filter);
    const canModify = owned && showActions;

    return (
      <div
        key={filter.id}
        className={clsx(
          'group flex items-center justify-between rounded-lg border transition-colors',
          activeFilterId === filter.id
            ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
            : 'border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-gray-600 dark:hover:bg-gray-700/60'
        )}
      >
        <button
          type="button"
          onClick={() => onSelect(filter)}
          className={clsx(
            'flex-1 text-left flex items-center gap-2.5 min-w-0',
            sizeClasses[size]
          )}
        >
          {/* Per-row visibility icon. Suppressed inside visibility groups,
              where the section header already conveys private vs. team. */}
          {showVisibilityIcon && filter.visibility && (
            <span
              className="flex-shrink-0 text-gray-400 dark:text-gray-500"
              title={filter.visibility === 'public' ? 'Shared with team' : 'Private'}
            >
              {filter.visibility === 'public' ? (
                <GlobeGlyph className="h-4 w-4" />
              ) : (
                <LockGlyph className="h-4 w-4" />
              )}
            </span>
          )}
          {/* Leading bullet — gives each stored filter a list marker. Shown
              inside visibility groups, where the per-row icon is suppressed. */}
          {!showVisibilityIcon && (
            <span
              aria-hidden
              className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600"
            />
          )}
          <span className="flex-1 truncate font-medium">{filter.name}</span>
          {filter.isDefault && (
            <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded dark:bg-blue-900/30 dark:text-blue-300">
              Default
            </span>
          )}
          <span className="flex-shrink-0 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-400">
            {getFilterCount(filter)} filter{getFilterCount(filter) !== 1 ? 's' : ''}
          </span>
        </button>

        {canModify && (onDelete || onSetDefault || onUnsetDefault || onEdit) && (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                const willOpen = menuOpenId !== filter.id;
                setMenuAnchor(willOpen ? e.currentTarget.getBoundingClientRect() : null);
                setMenuOpenId(willOpen ? filter.id : null);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-500"
              aria-label="Filter actions"
            >
              &#8942;
            </button>

            {menuOpenId === filter.id && (
              <FilterActionsMenu
                filter={filter}
                anchorRect={menuAnchor}
                onEdit={onEdit}
                onDelete={(id) => setConfirmDeleteId(id)}
                onSetDefault={onSetDefault}
                onUnsetDefault={onUnsetDefault}
                onClose={() => {
                  setMenuOpenId(null);
                  setMenuAnchor(null);
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSectionHeader = (
    label: string,
    count: number,
    sectionKey: string,
    icon?: React.ReactNode,
  ) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <button
        type="button"
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center gap-1.5 w-full text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-2 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <svg
          className={clsx('h-3 w-3 transition-transform', isCollapsed ? '' : 'rotate-90')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {icon && <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">{icon}</span>}
        {label}
        <span className="text-gray-400 dark:text-gray-500 normal-case tracking-normal">({count})</span>
      </button>
    );
  };

  const searchInput = showSearch && (
    <div className="relative mb-2">
      <svg
        className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search filters..."
        className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
      {searchQuery && (
        <button
          type="button"
          onClick={() => setSearchQuery('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  // Group filters by visibility: Private (owner-only) vs. Shared with team.
  // Grouping on the visibility flag — not on ownership — keeps a private and a
  // public filter the same user created in distinct sections.
  if (groupByVisibility) {
    const privateFilters = filteredFilters.filter((f) => f.visibility !== 'public');
    const teamFilters = filteredFilters.filter((f) => f.visibility === 'public');
    // Counts from the unfiltered list so headers stay stable while searching.
    const privateCount = savedFilters.filter((f) => f.visibility !== 'public').length;
    const teamCount = savedFilters.filter((f) => f.visibility === 'public').length;

    return (
      <div className={clsx('space-y-2', className)}>
        {searchInput}
        <div className="max-h-80 overflow-y-auto space-y-4 pb-1">
          {privateCount > 0 && (
            <div>
              {renderSectionHeader('Private', privateCount, 'private', <LockGlyph className="h-3 w-3" />)}
              {!collapsedSections['private'] && (
                <div className="space-y-1">
                  {privateFilters.length > 0 ? (
                    privateFilters.map((f) => renderFilter(f, false))
                  ) : searchQuery ? (
                    <div className="text-xs text-gray-400 px-2 py-1 dark:text-gray-500">No matches</div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {teamCount > 0 && (
            <div>
              {renderSectionHeader('Shared with team', teamCount, 'team', <GlobeGlyph className="h-3 w-3" />)}
              {!collapsedSections['team'] && (
                <div className="space-y-1">
                  {teamFilters.length > 0 ? (
                    teamFilters.map((f) => renderFilter(f, false))
                  ) : searchQuery ? (
                    <div className="text-xs text-gray-400 px-2 py-1 dark:text-gray-500">No matches</div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {searchQuery && privateFilters.length === 0 && teamFilters.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-2 dark:text-gray-500">No filters match "{searchQuery}"</div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        {confirmDeleteId && onDelete && (
          <ConfirmDeleteDialog
            filterName={savedFilters.find((f) => f.id === confirmDeleteId)?.name || ''}
            isPublic={savedFilters.find((f) => f.id === confirmDeleteId)?.visibility === 'public'}
            onConfirm={() => {
              onDelete(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
            onCancel={() => setConfirmDeleteId(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={clsx('space-y-2', className)}>
      {searchInput}
      <div className="max-h-60 overflow-y-auto space-y-0.5 pb-1">
        {filteredFilters.map((f) => renderFilter(f))}
        {searchQuery && filteredFilters.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-2 dark:text-gray-500">No filters match "{searchQuery}"</div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {confirmDeleteId && onDelete && (
        <ConfirmDeleteDialog
          filterName={savedFilters.find((f) => f.id === confirmDeleteId)?.name || ''}
          isPublic={savedFilters.find((f) => f.id === confirmDeleteId)?.visibility === 'public'}
          onConfirm={() => {
            onDelete(confirmDeleteId);
            setConfirmDeleteId(null);
          }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}

interface ConfirmDeleteDialogProps {
  filterName: string;
  isPublic?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDeleteDialog({ filterName, isPublic, onConfirm, onCancel }: ConfirmDeleteDialogProps) {
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-25 z-40" onClick={onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm">
          <div className="p-4 space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-white">Delete saved filter</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isPublic
                ? `This is a shared filter used by your team. Delete "${filterName}"?`
                : `Delete filter "${filterName}"? This cannot be undone.`}
            </p>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

interface FilterActionsMenuProps {
  filter: SavedFilter;
  anchorRect: DOMRect | null;
  onEdit?: (filter: SavedFilter) => void;
  onDelete?: (filterId: string) => void;
  onSetDefault?: (filterId: string) => void;
  onUnsetDefault?: (filterId: string) => void;
  onClose: () => void;
}

const MENU_WIDTH = 144; // w-36
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

function FilterActionsMenu({
  filter,
  anchorRect,
  onEdit,
  onDelete,
  onSetDefault,
  onUnsetDefault,
  onClose,
}: FilterActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Position the menu relative to the trigger button. Because it's rendered in
  // a portal with `position: fixed`, it escapes the saved-filters scroll
  // container (which would otherwise clip it). Flip upward when there isn't
  // enough room below the button.
  useLayoutEffect(() => {
    if (!anchorRect || !menuRef.current) return;
    const menuHeight = menuRef.current.getBoundingClientRect().height;

    let top = anchorRect.bottom + MENU_GAP;
    if (top + menuHeight > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, anchorRect.top - menuHeight - MENU_GAP);
    }

    let left = anchorRect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    setPos({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // A fixed-position menu would drift if the page scrolls/resizes while open,
  // so close it on either event.
  useEffect(() => {
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="fixed w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
      style={{
        top: pos?.top ?? anchorRect.bottom + MENU_GAP,
        left: pos?.left ?? anchorRect.right - MENU_WIDTH,
        // Hide until measured so it doesn't flash at the wrong spot.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {onEdit && (
        <button
          type="button"
          onClick={() => {
            onEdit(filter);
            onClose();
          }}
          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Edit
        </button>
      )}
      {onSetDefault && !filter.isDefault && (
        <button
          type="button"
          onClick={() => {
            onSetDefault(filter.id);
            onClose();
          }}
          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Set as default
        </button>
      )}
      {onUnsetDefault && filter.isDefault && (
        <button
          type="button"
          onClick={() => {
            onUnsetDefault(filter.id);
            onClose();
          }}
          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Remove as default
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={() => {
            onDelete(filter.id);
            onClose();
          }}
          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          Delete
        </button>
      )}
    </div>,
    document.body,
  );
}

// Save filter dialog
export interface SaveFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, options: { isDefault: boolean; visibility: 'private' | 'public' }) => void;
  initialName?: string;
  initialIsDefault?: boolean;
  initialVisibility?: 'private' | 'public';
  showVisibility?: boolean;
  title?: string;
  saveButtonText?: string;
}

export function SaveFilterDialog({
  isOpen,
  onClose,
  onSave,
  initialName = '',
  initialIsDefault = false,
  initialVisibility = 'private',
  showVisibility = true,
  title = 'Save filter',
  saveButtonText = 'Save',
}: SaveFilterDialogProps) {
  const [name, setName] = useState(initialName);
  const [isDefault, setIsDefault] = useState(initialIsDefault);
  const [visibility, setVisibility] = useState<'private' | 'public'>(initialVisibility);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setIsDefault(initialIsDefault);
      setVisibility(initialVisibility);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, initialName, initialIsDefault, initialVisibility]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim(), { isDefault, visibility });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-white">{title}</h3>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Filter name
              </label>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter a name..."
                maxLength={100}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>

            {showVisibility && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Visibility
                </label>
                <div className="flex gap-3">
                  <label
                    className={clsx(
                      'flex-1 flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-colors',
                      visibility === 'private'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={visibility === 'private'}
                      onChange={() => setVisibility('private')}
                      className="h-4 w-4 text-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Private</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Only you</div>
                    </div>
                  </label>
                  <label
                    className={clsx(
                      'flex-1 flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-colors',
                      visibility === 'public'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={visibility === 'public'}
                      onChange={() => setVisibility('public')}
                      className="h-4 w-4 text-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">Team</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">All agents</div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-700"
              />
              <span className="text-gray-700 dark:text-gray-300">Set as default filter</span>
            </label>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!name.trim()}
                className={clsx(
                  'flex-1 px-4 py-2 text-sm rounded-lg transition-colors',
                  name.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                )}
              >
                {saveButtonText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// Save filter button with dropdown
export interface SaveFilterButtonProps {
  onSave: (name: string, options: { isDefault: boolean; visibility: 'private' | 'public' }) => void;
  savedFilters?: SavedFilter[];
  onSelectExisting?: (filter: SavedFilter) => void;
  buttonText?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  showVisibility?: boolean;
  className?: string;
}

export function SaveFilterButton({
  onSave,
  savedFilters = [],
  onSelectExisting,
  buttonText = 'Save filter',
  size = 'md',
  disabled = false,
  showVisibility = true,
  className,
}: SaveFilterButtonProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => {
          if (savedFilters.length > 0 && onSelectExisting) {
            setIsDropdownOpen(!isDropdownOpen);
          } else {
            setIsDialogOpen(true);
          }
        }}
        disabled={disabled}
        className={clsx(
          'inline-flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-lg',
          'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
          sizeClasses[size],
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 3H7a2 2 0 00-2 2v14l5-3 5 3V5a2 2 0 00-2-2z" />
        </svg>
        <span>{buttonText}</span>
        {savedFilters.length > 0 && onSelectExisting && (
          <span className="text-gray-400 dark:text-gray-500">&#9660;</span>
        )}
      </button>

      {isDropdownOpen && savedFilters.length > 0 && onSelectExisting && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                setIsDialogOpen(true);
              }}
              className="w-full text-left px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
            >
              + Save as new filter
            </button>
          </div>
          <div className="p-2 max-h-48 overflow-auto">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1">
              Update existing
            </div>
            {savedFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => {
                  onSelectExisting(filter);
                  setIsDropdownOpen(false);
                }}
                className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <SaveFilterDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={onSave}
        showVisibility={showVisibility}
      />
    </div>
  );
}

// Complete saved filters section
export interface SavedFiltersSectionProps {
  savedFilters: SavedFilter[];
  activeFilterId?: string;
  currentUserId?: string;
  onSelect: (filter: SavedFilter) => void;
  onSave: (name: string, options: { isDefault: boolean; visibility: 'private' | 'public' }) => void;
  onDelete?: (filterId: string) => void;
  onSetDefault?: (filterId: string) => void;
  onUnsetDefault?: (filterId: string) => void;
  onEdit?: (filter: SavedFilter) => void;
  title?: string;
  showSaveButton?: boolean;
  showVisibility?: boolean;
  className?: string;
}

export function SavedFiltersSection({
  savedFilters,
  activeFilterId,
  currentUserId,
  onSelect,
  onSave,
  onDelete,
  onSetDefault,
  onUnsetDefault,
  onEdit,
  title = 'Saved filters',
  showSaveButton = true,
  showVisibility = true,
  className,
}: SavedFiltersSectionProps) {
  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {title}
          {savedFilters.length > 0 && (
            <span className="ml-1.5 text-gray-400 dark:text-gray-500 font-normal">({savedFilters.length})</span>
          )}
        </h4>
        {showSaveButton && (
          <SaveFilterButton
            onSave={onSave}
            size="sm"
            showVisibility={showVisibility}
          />
        )}
      </div>

      <SavedFiltersList
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        currentUserId={currentUserId}
        onSelect={onSelect}
        onDelete={onDelete}
        onSetDefault={onSetDefault}
        onUnsetDefault={onUnsetDefault}
        onEdit={onEdit}
        size="md"
        groupByVisibility
      />
    </div>
  );
}
